import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import EditableMentionSpan from './EditableMentionSpan';
import MentionErrorBoundary from './MentionErrorBoundary';
import RichEditorErrorBoundary from './RichEditorErrorBoundary';
import PlainTextEditor from './PlainTextEditor';
import { 
  convertMarkupToEditableHTML,
  convertEditableHTMLToMarkup,
  insertMentionAtCursor,
  getCursorPosition,
  setCursorPosition,
  getCursorMentionSpan,
  preserveCursorPosition,
  detectMentionTrigger,
  parseMentions,
  handleMentionDeletion,
  cleanupEditorContent,
  handleClickPositioning
} from '../../utils/mentionUtils';
import { 
  isMobileDevice, 
  scrollIntoViewWithKeyboard, 
  getViewportHeight 
} from '../../utils/mobileUtils';
import { mobileFocusManager } from '../../utils/MobileFocusManager';

const RichMentionEditor = forwardRef(({ 
  value = '', 
  onChange, 
  placeholder = 'Enter your content...', 
  maxLength = 5000,
  className = '',
  error = false,
  disabled = false,
  onBlur,
  onKeyUp,
  onKeyDown,
  autoFocus = false,
  rows = 6,
  onMentionTrigger,
  onMentionsChange
}, ref) => {
  const editorRef = useRef(null);
  const touchStartRef = useRef(null);
  const fallbackRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [hasError, setHasError] = useState(false);
  const [supportsContentEditable, setSupportsContentEditable] = useState(true);
  
  // Mobile detection (memoized for performance)
  const isMobile = useMemo(() => isMobileDevice(), []);

  // Check contentEditable support
  useEffect(() => {
    try {
      const testDiv = document.createElement('div');
      testDiv.contentEditable = true;
      const isSupported = testDiv.contentEditable === 'true' || testDiv.contentEditable === true;
      setSupportsContentEditable(isSupported);
      
      // Additional check for known problematic browsers
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('msie') || userAgent.includes('trident')) {
        setSupportsContentEditable(false);
      }
    } catch (error) {
      console.debug('ContentEditable support check failed:', error);
      setSupportsContentEditable(false);
    }
  }, []);

  // Expose ref to parent
  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    blur: () => editorRef.current?.blur(),
    get selectionStart() {
      return getCursorPosition(editorRef.current);
    },
    set selectionStart(position) {
      setCursorPosition(editorRef.current, position);
    },
    get selectionEnd() {
      return getCursorPosition(editorRef.current);
    },
    set selectionEnd(position) {
      setCursorPosition(editorRef.current, position);
    },
    setSelectionRange: (start, end) => {
      setCursorPosition(editorRef.current, start);
    },
    get value() {
      return convertEditableHTMLToMarkup(editorRef.current?.innerHTML || '');
    },
    set value(newValue) {
      if (editorRef.current) {
        // Set flag to prevent external updates during value setting
        isInternalUpdateRef.current = true;
        editorRef.current.innerHTML = convertMarkupToEditableHTML(newValue);
        setTimeout(() => {
          isInternalUpdateRef.current = false;
        }, 10);
      }
    },
    insertMention
  }));

  // Memoized content conversion with performance monitoring
  const getDisplayHTML = useCallback((content) => {
    const startTime = performance.now();
    
    try {
      const result = convertMarkupToEditableHTML(content || '');
      
      const endTime = performance.now();
      const conversionTime = endTime - startTime;
      
      if (conversionTime > 20) {
        console.debug(`Slow markup to HTML conversion: ${conversionTime}ms`);
      }
      
      return result;
    } catch (error) {
      console.debug('Error converting markup to HTML:', error);
      setHasError(true);
      return content || '';
    }
  }, []);

  // Memoized content conversion with performance monitoring
  const getRawContent = useCallback((html) => {
    const startTime = performance.now();
    
    try {
      const result = convertEditableHTMLToMarkup(html || '');
      
      const endTime = performance.now();
      const conversionTime = endTime - startTime;
      
      if (conversionTime > 20) {
        console.debug(`Slow HTML to markup conversion: ${conversionTime}ms`);
      }
      
      return result;
    } catch (error) {
      console.debug('Error converting HTML to markup:', error);
      setHasError(true);
      return html || '';
    }
  }, []);

  // Handle content changes with error recovery and performance monitoring
  const handleInput = useCallback((e) => {
    if (isComposing || !onChange) return;
    
    const startTime = performance.now();
    
    try {
      // Set flag to prevent external content updates during internal changes
      isInternalUpdateRef.current = true;
      
      const html = e.target.innerHTML;
      const rawContent = getRawContent(html);
      
      // Update mentions tracking
      const mentions = parseMentions(rawContent);
      onMentionsChange?.(mentions);
      
      onChange(rawContent);
      
      // Performance monitoring
      const endTime = performance.now();
      const processingTime = endTime - startTime;
      
      if (processingTime > 50) {
        console.debug(`Slow input processing: ${processingTime}ms`);
      }
    } catch (error) {
      console.debug('Error handling input:', error);
      setHasError(true);
      
      // Fallback to plain text content
      const plainText = e.target.textContent || e.target.innerText || '';
      onChange(plainText);
    } finally {
      // Reset flag after a short delay to allow for any immediate updates
      setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 10);
    }
  }, [isComposing, onChange, getRawContent, onMentionsChange]);

  // Handle paste events
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    
    // Get plain text from clipboard
    const text = e.clipboardData.getData('text/plain');
    
    // Insert as plain text
    document.execCommand('insertText', false, text);
    
    // Trigger input event
    handleInput(e);
  }, [handleInput]);

  // Handle key events
  const handleKeyDown = useCallback((e) => {
    // Handle mention deletion
    const mentionSpan = getCursorMentionSpan(editorRef.current);
    if (mentionSpan && (e.key === 'Backspace' || e.key === 'Delete')) {
      e.preventDefault();
      
      // Use enhanced mention deletion logic
      handleMentionDeletion(editorRef.current, mentionSpan, e.key === 'Delete');
      
      // Clean up editor content
      cleanupEditorContent(editorRef.current);
      
      // Trigger input event
      setTimeout(() => handleInput(e), 0);
      return;
    }
    
    // Prevent line breaks entirely for forum posts/replies
    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }
    
    // Mobile-specific key handling
    if (isMobile) {
      // Handle virtual keyboard navigation
      if (e.key === 'Tab') {
        e.preventDefault();
        // Move focus to next focusable element
        const focusableElements = document.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const currentIndex = Array.from(focusableElements).indexOf(editorRef.current);
        const nextElement = focusableElements[currentIndex + 1];
        if (nextElement) {
          nextElement.focus();
        }
        return;
      }
    }
    
    // Call parent key handler
    onKeyDown?.(e);
  }, [handleInput, rows, onKeyDown, isMobile]);

  // Handle key up for mention detection
  const handleKeyUp = useCallback((e) => {
    if (isComposing) return;
    
    const cursorPosition = getCursorPosition(editorRef.current);
    const rawContent = getRawContent(editorRef.current?.innerHTML || '');
    
    // Detect mention trigger
    const trigger = detectMentionTrigger(rawContent, cursorPosition);
    
    if (onMentionTrigger) {
      onMentionTrigger(trigger);
    }
    
    onKeyUp?.(e);
  }, [isComposing, getRawContent, onMentionTrigger, onKeyUp]);

  // Handle mouse up to ensure proper cursor positioning after clicks
  const handleMouseUp = useCallback((e) => {
    if (disabled) return;
    
    // Small delay to let browser finish processing the click
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      let node = range.startContainer;
      
      // Check if cursor ended up inside a mention
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && 
            node.classList.contains('editable-mention')) {
          // Move cursor after the mention
          const newRange = document.createRange();
          newRange.setStartAfter(node);
          newRange.setEndAfter(node);
          selection.removeAllRanges();
          selection.addRange(newRange);
          break;
        }
        node = node.parentNode;
      }
    }, 0);
  }, [disabled]);

  // Handle touch events for better mobile interaction
  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      timestamp: Date.now()
    };
  }, [isMobile]);
  
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile || !touchStartRef.current) return;
    
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
      timestamp: Date.now()
    };
    
    // Check if it's a tap (not a scroll)
    const deltaX = Math.abs(touchEnd.x - touchStartRef.current.x);
    const deltaY = Math.abs(touchEnd.y - touchStartRef.current.y);
    const deltaTime = touchEnd.timestamp - touchStartRef.current.timestamp;
    
    const isTap = deltaX < 10 && deltaY < 10 && deltaTime < 300;
    
    if (isTap && editorRef.current) {
      // Focus immediately without delay for better responsiveness
      editorRef.current.focus();
      
      // Scroll into view with keyboard compensation
      scrollIntoViewWithKeyboard(editorRef.current, 100);
    }
    
    touchStartRef.current = null;
  }, [isMobile]);

  // Handle focus
  const handleFocus = useCallback((e) => {
    setIsFocused(true);
    
    if (isMobile) {
      // Set keyboard visible state
      setKeyboardVisible(true);
      
      // Ensure proper mobile focus
      const element = e.target;
      setTimeout(() => {
        if (element && element.focus) {
          element.focus();
          
          // Position cursor at end for mobile
          try {
            setCursorPosition(element, getRawContent(element.innerHTML).length);
          } catch (error) {
            console.debug('Failed to position cursor:', error);
          }
        }
      }, 50);
      
      // Scroll into view with keyboard compensation
      scrollIntoViewWithKeyboard(element, 300);
    }
  }, [isMobile, getRawContent]);

  // Handle blur
  const handleBlur = useCallback((e) => {
    setIsFocused(false);
    setKeyboardVisible(false);
    onBlur?.(e);
  }, [onBlur]);

  // Handle composition events for IME input
  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);

  const handleCompositionEnd = useCallback((e) => {
    setIsComposing(false);
    // Trigger input after composition ends
    setTimeout(() => handleInput(e), 0);
  }, [handleInput]);

  // Insert mention at cursor position with error handling
  const insertMention = useCallback((user, startPosition, endPosition) => {
    if (!editorRef.current || !user) return;
    
    try {
      // Set flag to prevent external content updates during mention insertion
      isInternalUpdateRef.current = true;
      
      preserveCursorPosition(editorRef.current, () => {
        insertMentionAtCursor(editorRef.current, user, startPosition, endPosition);
      });
      
      // Trigger input event to update content
      const event = new Event('input', { bubbles: true });
      editorRef.current.dispatchEvent(event);
    } catch (error) {
      console.debug('Error inserting mention:', error);
      setHasError(true);
      
      // Fallback: insert plain text mention
      try {
        const mentionText = `@${user.displayName}`;
        const currentContent = editorRef.current.textContent || '';
        const beforeMention = currentContent.substring(0, startPosition);
        const afterMention = currentContent.substring(endPosition);
        const newContent = beforeMention + mentionText + ' ' + afterMention;
        
        editorRef.current.textContent = newContent;
        onChange?.(newContent);
      } catch (fallbackError) {
        console.error('Fallback mention insertion failed:', fallbackError);
      }
    } finally {
      // Reset flag after a short delay
      setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 10);
    }
  }, [onChange]);

  // Track if content is being updated internally to prevent external updates
  const isInternalUpdateRef = useRef(false);

  // Set initial content when component mounts
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = getDisplayHTML(value);
    }
  }, []);

  // Update editor content when value prop changes (only when not focused and not during internal updates)
  useEffect(() => {
    if (editorRef.current && !isFocused && !isInternalUpdateRef.current) {
      const currentHTML = editorRef.current.innerHTML;
      const expectedHTML = getDisplayHTML(value);
      
      if (currentHTML !== expectedHTML) {
        editorRef.current.innerHTML = expectedHTML;
      }
    }
  }, [value, getDisplayHTML, isFocused]);

  // Mobile keyboard detection
  useEffect(() => {
    if (!isMobile) return;
    
    const handleResize = () => {
      const currentHeight = getViewportHeight();
      setViewportHeight(currentHeight);
      
      // Detect keyboard visibility
      const screenHeight = window.screen.height;
      let keyboardThreshold = screenHeight * 0.75;
      
      // iOS Safari has different behavior
      if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
        keyboardThreshold = screenHeight * 0.6;
      }
      
      // Android Chrome
      if (navigator.userAgent.includes('Chrome') && navigator.userAgent.includes('Android')) {
        keyboardThreshold = screenHeight * 0.7;
      }
      
      const isKeyboardVisible = currentHeight < keyboardThreshold;
      setKeyboardVisible(isKeyboardVisible);
    };
    
    // Use different event listeners based on browser support
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isMobile]);

  // Monitor selection changes to prevent cursor from being inside mentions
  useEffect(() => {
    if (!editorRef.current) return;
    
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection.rangeCount || !editorRef.current) return;
      
      const range = selection.getRangeAt(0);
      let node = range.startContainer;
      
      // Check if cursor is inside a mention
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && 
            node.classList.contains('editable-mention')) {
          // Move cursor after the mention
          const newRange = document.createRange();
          newRange.setStartAfter(node);
          newRange.setEndAfter(node);
          selection.removeAllRanges();
          selection.addRange(newRange);
          break;
        }
        node = node.parentNode;
      }
    };
    
    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  // Auto-focus handling with mobile support
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      if (isMobile) {
        // Delay focus to ensure DOM is ready
        const timer = setTimeout(async () => {
          if (editorRef.current) {
            try {
              await mobileFocusManager.focusWithKeyboard(editorRef.current);
            } catch (error) {
              console.debug('Auto-focus failed:', error);
              
              // Fallback for browsers that don't support the mobile focus manager
              editorRef.current.focus();
              
              // Additional fallback for iOS Safari
              if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
                setTimeout(() => {
                  if (editorRef.current) {
                    editorRef.current.click();
                    editorRef.current.focus();
                  }
                }, 50);
              }
            }
          }
        }, 100);
        
        return () => clearTimeout(timer);
      } else {
        const timer = setTimeout(() => {
          editorRef.current?.focus();
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [autoFocus, isMobile]);

  // Get current text length
  const getCurrentLength = () => {
    const rawContent = getRawContent(editorRef.current?.innerHTML || '');
    return rawContent.length;
  };

  // Expose methods with error handling
  useImperativeHandle(ref, () => {
    // If we have errors or don't support contentEditable, expose fallback methods
    if (hasError || !supportsContentEditable) {
      return fallbackRef.current || {
        focus: () => fallbackRef.current?.focus(),
        blur: () => fallbackRef.current?.blur(),
        get selectionStart() {
          return fallbackRef.current?.selectionStart || 0;
        },
        set selectionStart(position) {
          if (fallbackRef.current) {
            fallbackRef.current.selectionStart = position;
          }
        },
        get selectionEnd() {
          return fallbackRef.current?.selectionEnd || 0;
        },
        set selectionEnd(position) {
          if (fallbackRef.current) {
            fallbackRef.current.selectionEnd = position;
          }
        },
        setSelectionRange: (start, end) => {
          fallbackRef.current?.setSelectionRange?.(start, end);
        },
        get value() {
          return fallbackRef.current?.value || '';
        },
        set value(newValue) {
          if (fallbackRef.current) {
            fallbackRef.current.value = newValue;
          }
        },
        insertMention: () => {
          console.debug('Rich mention insertion not available in fallback mode');
        }
      };
    }

    return {
      insertMention,
      focus: () => {
        try {
          editorRef.current?.focus();
        } catch (error) {
          console.debug('Focus error:', error);
        }
      },
      blur: () => {
        try {
          editorRef.current?.blur();
        } catch (error) {
          console.debug('Blur error:', error);
        }
      },
      get selectionStart() {
        try {
          return getCursorPosition(editorRef.current);
        } catch (error) {
          console.debug('Selection start error:', error);
          return 0;
        }
      },
      set selectionStart(position) {
        try {
          setCursorPosition(editorRef.current, position);
        } catch (error) {
          console.debug('Set selection start error:', error);
        }
      },
      get selectionEnd() {
        try {
          return getCursorPosition(editorRef.current);
        } catch (error) {
          console.debug('Selection end error:', error);
          return 0;
        }
      },
      set selectionEnd(position) {
        try {
          setCursorPosition(editorRef.current, position);
        } catch (error) {
          console.debug('Set selection end error:', error);
        }
      },
      setSelectionRange: (start, end) => {
        try {
          setCursorPosition(editorRef.current, start);
        } catch (error) {
          console.debug('Set selection range error:', error);
        }
      },
      get value() {
        try {
          return getRawContent(editorRef.current?.innerHTML || '');
        } catch (error) {
          console.debug('Get value error:', error);
          return editorRef.current?.textContent || '';
        }
      },
      set value(newValue) {
        try {
          if (editorRef.current) {
            editorRef.current.innerHTML = getDisplayHTML(newValue);
          }
        } catch (error) {
          console.debug('Set value error:', error);
          if (editorRef.current) {
            editorRef.current.textContent = newValue;
          }
        }
      }
    };
  }, [insertMention, getRawContent, getDisplayHTML, hasError, supportsContentEditable]);

  // Fallback to PlainTextEditor if rich editor fails or isn't supported
  if (hasError || !supportsContentEditable) {
    return (
      <PlainTextEditor
        ref={fallbackRef}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        error={error}
        disabled={disabled}
        autoFocus={autoFocus}
        isMobile={isMobile}
        className={className}
        rows={rows}
      />
    );
  }

  return (
    <RichEditorErrorBoundary
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onKeyUp={onKeyUp}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      maxLength={maxLength}
      error={error}
      disabled={disabled}
      autoFocus={autoFocus}
      className={className}
      rows={rows}
    >
      <MentionErrorBoundary fallbackType="inline">
      <div 
        className={`border rounded-md ${
          isFocused ? 'border-purple-500 ring-1 ring-purple-500' : 
          error ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
        } ${className}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Rich content editor */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning={true}
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onMouseUp={handleMouseUp}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onTouchStart={(e) => {
            // Prevent double-tap zoom and improve touch responsiveness
            if (isMobile) {
              e.stopPropagation();
            }
          }}
          onClick={(e) => {
            if (disabled) return;
            
            // Handle click positioning for mentions - this may prevent default behavior
            const positioningHandled = handleClickPositioning(e.currentTarget, e);
            
            // Only do additional focus handling if positioning wasn't handled
            if (!positioningHandled) {
              // Always focus immediately on click for mobile
              if (isMobile) {
                e.currentTarget.focus();
                
                // Additional handling for mobile browsers to ensure proper focus
                if (!isFocused) {
                  // Use requestAnimationFrame for better timing
                  requestAnimationFrame(() => {
                    if (e.currentTarget) {
                      e.currentTarget.focus();
                      
                      // For iOS Safari, ensure cursor positioning
                      if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone')) {
                        setTimeout(() => {
                          if (e.currentTarget) {
                            e.currentTarget.focus();
                            // Only position at end if not already positioned
                            const selection = window.getSelection();
                            if (!selection.rangeCount) {
                              const content = getRawContent(e.currentTarget.innerHTML);
                              setCursorPosition(e.currentTarget, content.length);
                            }
                          }
                        }, 10);
                      }
                    }
                  });
                }
              } else {
                // For desktop, just ensure focus
                e.currentTarget.focus();
              }
            }
          }}
          role="textbox"
          aria-multiline="false"
          aria-label="Rich content editor with mention support"
          aria-describedby={!isMobile ? "editor-help" : undefined}
          aria-required="false"
          aria-invalid={error}
          aria-expanded={false}
          aria-autocomplete="list"
          data-placeholder={placeholder}
          className={`
            w-full p-3 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md
            min-h-[${rows * 1.5}rem] max-h-[300px] overflow-y-auto
            ${disabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}
            ${isMobile ? 'touch-manipulation' : ''}
          `}
          style={{ 
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            ...(isMobile && {
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              WebkitUserSelect: 'text',
              userSelect: 'text'
            })
          }}

        />

        {/* Character count - Hidden on mobile to save space */}
        {!isMobile && (
          <div 
            id="editor-help"
            className="flex justify-between items-center px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-xs"
            role="status"
            aria-live="polite"
          >
            <div className="text-gray-500 dark:text-gray-400">
              Rich text with mentions - Use @ to mention users. Line breaks are not allowed.
            </div>
            <div 
              className={`${
                getCurrentLength() > maxLength * 0.9 
                  ? getCurrentLength() > maxLength 
                    ? 'text-red-500' 
                    : 'text-yellow-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              aria-label={`${getCurrentLength()} of ${maxLength} characters used${
                getCurrentLength() > maxLength ? '. Character limit exceeded.' : ''
              }`}
              role="status"
            >
              {getCurrentLength()}/{maxLength}
            </div>
          </div>
        )}
        
        {/* Screen reader only character count and instructions for mobile */}
        {isMobile && (
          <>
            <div className="sr-only" aria-live="polite">
              {getCurrentLength() > maxLength * 0.9 && (
                `${getCurrentLength()} of ${maxLength} characters used${
                  getCurrentLength() > maxLength ? '. Character limit exceeded.' : ''
                }`
              )}
            </div>
            <div className="sr-only">
              Rich text editor with mention support. Type @ followed by a name to mention users. 
              Line breaks are not allowed. Use Tab to navigate to other elements.
            </div>
          </>
        )}

        {/* Mobile-specific styles */}
        {isMobile && (
          <style jsx>{`
            .touch-manipulation {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
            
            /* Improve mention tap targets on mobile */
            .editable-mention {
              min-height: 44px;
              display: inline-flex;
              align-items: center;
              padding: 0 4px;
              margin: 0 -4px;
            }
            
            /* Prevent zoom on double tap */
            [contenteditable] {
              touch-action: manipulation;
              -webkit-touch-callout: none;
              -webkit-user-select: text;
              user-select: text;
            }
          `}</style>
        )}

        {/* Placeholder styling */}
        <style jsx>{`
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9CA3AF;
            pointer-events: none;
          }
          .dark [contenteditable]:empty:before {
            color: #6B7280;
          }
        `}</style>
      </div>
    </MentionErrorBoundary>
    </RichEditorErrorBoundary>
  );
});

RichMentionEditor.displayName = 'RichMentionEditor';

export default RichMentionEditor;